import { useEffect, useState, useCallback } from 'react'
import { Grid, Box, Divider, Button, CircularProgress, InputAdornment, IconButton, OutlinedInput, Chip, Avatar } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { fetchBranchOrders, getCartItems, placeOrder, increamentOrder, deleteOrderItems, deleteEditCartItems } from '../../functions/orders';
import { useTheme, useMediaQuery } from '@mui/material';
import BackButton from '../../utils/BackButton';
import ModalLayout from '../../components/modals/Layout';
import toast from 'react-hot-toast';
import CartSkeleton from '../../components/Skeleton/CartSkeleton';
import CartCard from '../../components/Cards/CartCard';
import Header from './Header';
import MenuItems from './Menu';
import CloseIcon from '@mui/icons-material/Close';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RestaurantIcon from '@mui/icons-material/Restaurant';

function useCartItems(branchId, orderNo) {
    const [cartItems, setCartItems] = useState([]);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            const ordersRes = await fetchBranchOrders(branchId);
            const order_with_this_number = ordersRes?.data?.data.find(el => el.id === orderNo)
            setOrders(order_with_this_number);

            const cartRes = await getCartItems(orderNo);
            setCartItems(cartRes.data.data);
        } catch (error) {
            toast.error('Error fetching cart items');
        } finally {
            setLoading(false);
        }
    }, [branchId, orderNo]);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    return [cartItems, loading, fetchItems, orders];
}

const RenderCart = ({ handleQuantity, setIsDeleting, qLoading, setDeleteAction, deleteAction, ...props }) => (
    <CartCard
        handleQuantity={handleQuantity}
        setIsDeleting={setIsDeleting}
        qLoading={qLoading}
        setDeleteAction={setDeleteAction}
        deleteAction={deleteAction}
        {...props}
    />
);

const EditOrder = () => {
    const navigate = useNavigate();
    const { orderNo } = useParams();
    const {
        branch: { currentBranch: { restaurant_code, id: branchId } },
        auth: { user: { id: user_id } }
    } = useSelector((state) => state);

    const [cartItems, cartLoading, handleGetCartItems, orders] = useCartItems(branchId, orderNo);
    const [searchValue, setSearchValue] = useState(null);
    const [qLoading, setQLoading] = useState({ food_code: null, action: null });
    const [placeOrderLoading, setPlaceOrderLoading] = useState(false);
    const [deleteAction, setDeleteAction] = useState({ item: null, loading: false, id: null });
    const [isDeleting, setIsDeleting] = useState(false);
    const [activeTab, setActiveTab] = useState("cart");

    const orderDetails = orders.order_details;
    const theme = useTheme();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

    //increament or decreament quantity
    const handleQuantity = useCallback((food_code, action, quantity) => {
        if (quantity === 1 && action === 'decrement') {
            toast.error("Quantity cannot be less than one!");
            return;
        }
        setQLoading({ food_code, action });
        increamentOrder({ action, food_code }, orderNo)
            .then(res => {
                toast.success(res.data.message);
                handleGetCartItems();
            })
            .catch(err => {
                toast.error(err.data.message);
            })
            .finally(() => setQLoading({ food_code: null, action: null }));
    }, [orderNo, handleGetCartItems]);

    //delete cart item
    const handleDelete = useCallback(() => {
        const { item: f, id } = deleteAction;
        setDeleteAction({ ...deleteAction, loading: true });
        const updatedOrderDetails = orderDetails.filter(y => y.order_list.food__food_name !== f);
        const updatedAmount = updatedOrderDetails.reduce((acc, q) => acc + (q.order_list.quantity * q.order_list.food__price), 0);

        const payload = { order_details: updatedOrderDetails, amount: updatedAmount, user_id };

        const onSuccess = () => {
            toast.success("Item deleted successfully!");
            setIsDeleting(false);
            handleGetCartItems();
        };

        const onError = (err) => {
            toast.error(err.data?.message);
            setIsDeleting(false);
        };

        const deleteFn = id ? deleteEditCartItems : deleteOrderItems;
        deleteFn(id || payload, orderNo).then(onSuccess).catch(onError).finally(() => {
            setDeleteAction({ item: null, loading: false, id: null });
        });
    }, [deleteAction, orderDetails, orderNo, user_id, handleGetCartItems]);

    //place order
    const handlePlaceOrder = useCallback(() => {
        setPlaceOrderLoading(true);
        const cartItemsIds = cartItems.map(cI => ({ id: cI.id }));
        const payload = { restaurant_code, user_id, cart_items: cartItemsIds };

        placeOrder(payload, orderNo)
            .then(() => {
                navigate("/dashboard/orders");
                toast.success("Successfully edited order");
            })
            .finally(() => setPlaceOrderLoading(false));
    }, [cartItems, navigate, orderNo, restaurant_code, user_id]);

    const cPrice = cartItems?.reduce((acc, item) => acc + item.bill_amount, 0);
    const oPrice = orderDetails?.reduce((acc, item) => acc + (item.order_list.food__price * item.order_list.quantity), 0);
    const tPrice = cPrice + oPrice;

    const RenderCartUI = () => {
        const renderCartItems = () => {
            if (cartLoading) {
                return Array(5).fill(0).map((_, index) => <CartSkeleton key={index} />);
            }

            return <>
                {orderDetails?.map(({ food_image, order_list }) => (
                    <RenderCart
                        key={order_list.food_code}
                        food__price={order_list.food__price}
                        food_image_json={food_image}
                        ordered_addons={order_list.ordered_addons}
                        food_name={order_list.food__food_name}
                        quantity={order_list.quantity}
                        handleQuantity={handleQuantity}
                        qLoading={qLoading}
                    />
                ))}
                {cartItems?.map(({ id, quantity, ordered_addons, food }) => (
                    <RenderCart
                        key={food.food_code}
                        id={id}
                        food__price={food.price}
                        food_image_json={food.food_image_json}
                        ordered_addons={ordered_addons}
                        food_name={food.food_name}
                        quantity={quantity}
                        food_code={food.food_code}
                        handleQuantity={handleQuantity}
                        qLoading={qLoading}
                    />
                ))}
            </>;
        };

        return (
            <Grid item lg={5} xs={12}>
                <Box display="flex" flexDirection="row" alignItems="center" marginTop={isLargeScreen ? 0 : 4}>
                    <h2 className='font-semibold text-xl'>Orders</h2>
                    <BackButton />
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", rowGap: 2, mt: 4 }}>
                    {renderCartItems()}
                </Box>

                <Divider sx={{ mt: 4, mb: 2 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 1.5 }}>
                    <div className='flex flex-row justify-between'>
                        <p>( {orderDetails?.length + cartItems.length} items )</p>
                        <p className='font-bold text-primary'>
                            {tPrice?.toLocaleString("en", { style: "currency", currency: "KSH", })}
                        </p>
                    </div>
                </Box>

                <Button
                    disabled={cartItems.length === 0}
                    variant="contained" sx={{ my: 4, width: "100%" }}
                    onClick={handlePlaceOrder}
                >
                    {placeOrderLoading ? <CircularProgress size={24} color='inherit' /> : "Checkout"}
                </Button>
            </Grid>
        );
    };

    return (
        <Box>
            {
                !isLargeScreen &&
                <Box sx={{ mt: 2, display: "flex", columnGap: 2 }}>
                    <Chip label="Menu Items"
                        sx={{ backgroundColor: activeTab === 'menu' && "#F4B315", color: activeTab === 'menu' && "white" }}
                        onClick={() => setActiveTab('menu')}
                        avatar={
                            <Avatar sx={{ backgroundColor: 'transparent' }}>
                                <RestaurantIcon sx={{ color: activeTab === 'menu' && "white", fontSize: 18 }} />
                            </Avatar>
                        }
                    />

                    <Chip label="Cart Items"
                        sx={{ backgroundColor: activeTab === 'cart' && "#F4B315", color: activeTab === 'cart' && "white" }}
                        onClick={() => setActiveTab('cart')}
                        avatar={
                            <Avatar sx={{ backgroundColor: 'transparent' }}>
                                <ShoppingCartIcon sx={{ color: activeTab === 'cart' && "white", fontSize: 18 }} />
                            </Avatar>
                        }
                    />
                </Box>
            }

            <Grid container>
                {
                    !isLargeScreen &&
                    <Grid container spacing={4}>
                        {
                            activeTab === 'menu' &&
                            <Grid item lg={7} xs={12} sx={{ display: "flex", flexDirection: "column", mb: 4 }}>
                                <OutlinedInput
                                    sx={{ width: "100%", mt: 2 }}
                                    size='small'
                                    placeholder='Search menu items'
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    value={searchValue || ''}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setSearchValue(null)}
                                                edge="end"
                                            >
                                                {searchValue && <CloseIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                />
                                <Box sx={{ mt: 4 }}>
                                    <MenuItems count={2} searchValue={searchValue} />
                                </Box>
                            </Grid>
                        }
                        {activeTab === 'cart' && <RenderCartUI />}
                    </Grid>
                }

                {
                    isLargeScreen &&
                    <Grid container spacing={4}>
                        <Grid item lg={7} xs={12} sx={{ display: "flex", flexDirection: "column", mb: 4 }}>
                            <Header searchValue={searchValue} setSearchValue={setSearchValue} />
                            <Box sx={{ mt: 4 }}>
                                <MenuItems count={3} searchValue={searchValue} />
                            </Box>
                        </Grid>
                        <RenderCartUI />
                    </Grid>
                }

            </Grid>

            <ModalLayout open={isDeleting} setOpen={setIsDeleting} title={`Delete Order Item`}>
                <div>
                    <p className="my-6">Are you sure you want to delete?</p>
                    <div className="flex gap-4 justify-end">
                        <button
                            onClick={() => {
                                setDeleteAction({ item: null, loading: false, id: null });
                                setIsDeleting(false)
                            }}
                            className="bg-gray-200 px-6 py-2 rounded">Cancel</button>
                        <button disabled={deleteAction.loading} onClick={handleDelete} className="flex items-center gap-3 bg-red-800 px-6 py-2 text-red-100 rounded">
                            Delete {deleteAction.loading && (<CircularProgress color="inherit" size="1.2rem" />)}
                        </button>
                    </div>
                </div>
            </ModalLayout>
        </Box>
    )
}

export default EditOrder